import React, { useState, useEffect, useContext, useRef } from 'react';
import SocketContext from '../../../../../../../NewWebSocket/components/WebSocketAdapter';
import './ResourceControl.css';
import { ResourceType } from '../../../../../../gameTypes';

interface ResourceDisplay {
  type: ResourceType;
  current: number;
  max: number;
}

interface ResourceControlProps {
  championId: string;
  primaryResource?: ResourceDisplay;
}

// Get color for different resource types
function getResourceColor(type: ResourceType): string {
  switch (type) {
    case "Mana":
      return "#060684"; // blue
    case "Energy":
      return "#e1c600"; // yellow
    case "Fury":
      return "#9b0000"; // red
    case "Flow":
      return "#ffffff"; // white
    case "Shield":
      return "#999999"; // gray
    default:
      return "#555555"; // fallback
  }
}

// Creates a functional wrapper to handle the "None" resource case
const ResourceControlWrapper: React.FC<ResourceControlProps> = (props) => {
  // Early return if no resource or type is None
  if (!props.primaryResource || props.primaryResource.type === "None") {
    return null;
  }
  
  // If resource exists, render the actual control
  return <ResourceControlInner {...props} />;
};

// Inner component that contains all the hooks and functionality
const ResourceControlInner: React.FC<ResourceControlProps> = ({
  championId,
  primaryResource
}) => {
  const socket = useContext(SocketContext);
  const resourceBarRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  
  // We know primaryResource exists at this point
  const resourceColor = getResourceColor(primaryResource!.type);
  const resourceName = primaryResource!.type;
  const currentResource = Math.round(primaryResource!.current);
  const maxResource = Math.round(primaryResource!.max);
  const resourcePercentage = Math.round((currentResource / maxResource) * 100);
  
  // Control states
  const [sliderPercentage, setSliderPercentage] = useState(resourcePercentage);
  const [resourceValue, setResourceValue] = useState(currentResource.toString());
  
  // Update control states when resource changes
  useEffect(() => {
    setSliderPercentage(resourcePercentage);
    setResourceValue(currentResource.toString());
  }, [currentResource, maxResource, resourcePercentage]);
  
  // Handler for setting resource by absolute value
  const handleResourceValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setResourceValue(value);
    if (!isNaN(parseInt(value, 10))) {
      const numericValue = parseInt(value, 10);
      setSliderPercentage(Math.min(Math.round((numericValue / maxResource) * 100), 100));
    }
  };
  
  // Handler for setting resource by percentage
  const handlePercentageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const percentage = parseInt(e.target.value, 10);
    setSliderPercentage(percentage);
    setResourceValue(Math.round(maxResource * percentage / 100).toString());
  };
  
  // Handler for applying the resource change
  const applyResourceChange = () => {
    if (!socket) return;
    
    console.log(`[ResourceControl] Setting ${primaryResource!.type} for champion ${championId} to ${resourceValue}`);
    socket.emit('set-champion-resource', {
      championId: championId,
      value: parseInt(resourceValue, 10) || 0,
      isPercentage: false,
      resourceType: primaryResource!.type
    });
  };

  // Spinner button handlers
  const incrementResource = () => {
    const newValue = Math.min(parseInt(resourceValue, 10) + 1, maxResource);
    setResourceValue(newValue.toString());
    setSliderPercentage(Math.round((newValue / maxResource) * 100));
  };

  const decrementResource = () => {
    const newValue = Math.max(parseInt(resourceValue, 10) - 1, 0);
    setResourceValue(newValue.toString());
    setSliderPercentage(Math.round((newValue / maxResource) * 100));
  };

  const incrementPercentage = () => {
    const newPercentage = Math.min(sliderPercentage + 1, 100);
    setSliderPercentage(newPercentage);
    setResourceValue(Math.round(maxResource * newPercentage / 100).toString());
  };

  const decrementPercentage = () => {
    const newPercentage = Math.max(sliderPercentage - 1, 0);
    setSliderPercentage(newPercentage);
    setResourceValue(Math.round(maxResource * newPercentage / 100).toString());
  };

  // Calculate resource percentage from click/touch position
  const calculatePercentageFromPosition = (clientX: number) => {
    if (!resourceBarRef.current) return;
    
    const rect = resourceBarRef.current.getBoundingClientRect();
    const position = clientX - rect.left; 
    const totalWidth = rect.width;
    
    // Calculate percentage based on position
    let percentage = Math.round((position / totalWidth) * 100);
    
    // Clamp percentage between 0 and 100
    percentage = Math.max(0, Math.min(100, percentage));
    
    // Update slider percentage and resource value
    setSliderPercentage(percentage);
    setResourceValue(Math.round(maxResource * percentage / 100).toString());
  };

  // Event handlers for mouse/touch interaction with resource bar
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsDragging(true);
    calculatePercentageFromPosition(e.clientX);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isDragging) {
      calculatePercentageFromPosition(e.clientX);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    setIsDragging(true);
    calculatePercentageFromPosition(e.touches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (isDragging) {
      calculatePercentageFromPosition(e.touches[0].clientX);
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // Setup global mouse/touch event listeners for dragging
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      setIsDragging(false);
    };

    const handleGlobalTouchEnd = () => {
      setIsDragging(false);
    };

    // Add global event listeners
    document.addEventListener('mouseup', handleGlobalMouseUp);
    document.addEventListener('touchend', handleGlobalTouchEnd);

    // Clean up
    return () => {
      document.removeEventListener('mouseup', handleGlobalMouseUp);
      document.removeEventListener('touchend', handleGlobalTouchEnd);
    };
  }, []);

  return (
    <div className="resource-control">
      {/* Resource display and controls */}
      <div className="resource-control-container">
        <div className="resource-input-row">
          <h4>{resourceName}</h4>
          <div className="resource-inputs">
            <span>Current {resourceName}</span>
            <div className="resource-input-container">
              <input
                type="number"
                min="0"
                max={maxResource}
                value={resourceValue}
                onChange={handleResourceValueChange}
                className="resource-value-input"
              />
              <div className="custom-spinner">
                <button className="spinner-button" onClick={incrementResource}></button>
                <button className="spinner-button spinner-down" onClick={decrementResource}></button>
              </div>
            </div>
            <span>/{maxResource}</span>
            <span>(</span>
            <div className="resource-input-container">
              <input
                type="number"
                min="0"
                max="100"
                value={sliderPercentage}
                onChange={handlePercentageChange}
                className="resource-percentage-input"
              />
              <div className="custom-spinner">
                <button className="spinner-button" onClick={incrementPercentage}></button>
                <button className="spinner-button spinner-down" onClick={decrementPercentage}></button>
              </div>
            </div>
            <span>%)</span>
          </div>
          <button 
            onClick={applyResourceChange}
            className="apply-button"
          >
            Apply
          </button>
        </div>
        
        {/* Interactive Resource bar */}
        <div 
          className="resource-bar"
          ref={resourceBarRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{ backgroundColor: '#333' }}
        >
          {/* Current resource */}
          <div 
            className="resource-fill"
            data-resource={resourceName}
            style={{ 
              width: `${Math.min(resourcePercentage, 100)}%`
            }}
          ></div>
          
          {/* Preview - Increasing resource */}
          {sliderPercentage > resourcePercentage && (
            <div 
              className="resource-preview-increase"
              data-resource={resourceName}
              style={{ 
                width: `${Math.min(sliderPercentage - resourcePercentage, 100)}%`,
                left: `${resourcePercentage}%`
              }}
            ></div>
          )}
          
          {/* Preview - Decreasing resource */}
          {sliderPercentage < resourcePercentage && (
            <div 
              className="resource-preview-decrease"
              style={{ 
                width: `${Math.min(resourcePercentage - sliderPercentage, 100)}%`,
                left: `${sliderPercentage}%`
              }}
            ></div>
          )}
          
          {/* Slider indicator dot */}
          <div 
            className="resource-slider-indicator"
            style={{ left: `calc(${sliderPercentage}% - 5px)` }}
          ></div>
        </div>
      </div>
    </div>
  );
};

// Export the wrapper component
export default ResourceControlWrapper; 